import { Upload } from 'lucide-react'
import { useMemo, useRef } from 'react'

import { UploadQueue } from '../uploads/UploadQueue'
import { shareTransport } from '../../upload/transport'
import { useUploadQueue } from '../../upload/useUploadQueue'
import { Banner, Button, ICON_STROKE } from '../../ui'

interface Props {
  sessionToken: string
  projectId: string
}

/**
 * El cliente manda archivos al estudio por el mismo enlace.
 *
 * Invierte la dirección del portal, que hasta ahora sólo entregaba. El caso que
 * lo pide no es exótico: la imprenta que necesita el arte final, el estudio de
 * arquitectura que espera el levantamiento, o el propio fotógrafo pidiendo las
 * fotos que hizo un invitado.
 *
 * Reutiliza el uploader ENTERO del estudio —troceo, reanudación por IndexedDB,
 * concurrencia según el dispositivo, reintentos— cambiando sólo el transporte.
 * Es justo lo que se ganó al extraer `UploadTransport`: aquí no hay ni una
 * línea de lógica de subida, y por eso una subida que se corta a media entrega
 * se reanuda igual que le pasa al estudio.
 */
export function SendFilesPanel({ sessionToken, projectId }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)

  // El transporte se crea UNA vez: `useUploadQueue` monta su manager en el
  // primer render y no vuelve a mirarlo, así que uno nuevo por render sería
  // silenciosamente ignorado — y parecería que funciona hasta que no.
  const transport = useMemo(() => shareTransport(sessionToken), [sessionToken])

  const { items, batchError, summary, enqueue, cancel } = useUploadQueue(projectId, transport)

  const pending = items.filter((item) => item.status !== 'done')

  return (
    <section className="mx-auto w-full max-w-page px-4 py-8 sm:px-6">
      <div className="rounded-2xl border border-line bg-surface p-5 sm:p-6">
        <h2 className="text-subhead font-semibold text-ink">Enviar archivos al estudio</h2>
        <p className="mt-2 max-w-prose text-body text-ink-muted">
          Se envían directamente y no hace falta crear ninguna cuenta. Si se corta la conexión,
          puedes volver a abrir este enlace y continuar donde se quedó.
        </p>

        <input
          ref={inputRef}
          type="file"
          multiple
          className="sr-only"
          onChange={(event) => {
            const files = [...(event.target.files ?? [])]
            if (files.length > 0) enqueue(files)
            // Se limpia para que elegir el MISMO archivo otra vez —tras un
            // fallo, que es el caso normal aquí— vuelva a disparar el change.
            event.target.value = ''
          }}
        />

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Button onClick={() => inputRef.current?.click()}>
            <Upload size={18} strokeWidth={ICON_STROKE} aria-hidden />
            Elegir archivos
          </Button>

          {summary.active > 0 ? (
            <span className="text-label text-ink-muted" role="status">
              Enviando… {summary.percent} %
            </span>
          ) : null}

          {summary.done > 0 && summary.active === 0 ? (
            <span className="text-label text-ink-muted" role="status">
              {summary.done} {summary.done === 1 ? 'archivo enviado' : 'archivos enviados'}
            </span>
          ) : null}
        </div>

        {batchError !== null ? (
          <div className="mt-4">
            <Banner tone="danger">{batchError}</Banner>
          </div>
        ) : null}

        {/*
          Sólo lo que sigue en marcha. Lo ya enviado se resume arriba en una
          línea: dejar treinta filas verdes acumuladas empuja el botón fuera de
          la pantalla justo cuando el cliente quiere añadir el siguiente lote.
        */}
        {pending.length > 0 ? (
          <div className="mt-5">
            <UploadQueue items={pending} onCancel={cancel} />
          </div>
        ) : null}
      </div>
    </section>
  )
}
